import { crsLeadSubmitted } from "@/lib/analytics-events";
import { useState, type FormEvent } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import {
 ArrowRight,
 Building2,
 CalendarCheck,
 CheckCircle2,
 ClipboardCheck,
 ClipboardList,
 Cpu,
 FileText,
 Gauge,
 HardHat,
 Handshake,
 Home,
 Landmark,
 Layers,
 Leaf,
 Loader2,
 PoundSterling,
 ShieldCheck,
 Sun,
 TreePine,
 Users,
 Wind,
 Wrench,
} from "lucide-react";

import { submitEnquiry } from "@/lib/forms.functions";
import { submissionSchema } from "@/lib/form-schema";
import { PageBreadcrumb } from "@/components/page-breadcrumb";
import { commercialBreadcrumbTrail } from "@/lib/breadcrumbs";
import { AccreditationStrip } from "@/components/site-blocks";

import crsLogo from "@/assets/crs-logo-white.webp";
import heroCommercialRetrofit from "@/assets/commercial-retrofit/hero-commercial-retrofit.webp";

const PHONE_LABEL = "0800 009 2992";
const PHONE_TEL = "tel:+448000092992";
const SOURCE_PAGE = "/commercial-retrofit";
const CAPABILITY_STATEMENT_HREF = "#bottom-cta";

export const COMMERCIAL_RETROFIT_FAQS = [
 {
 question: "Are you framework-ready for council and housing-association procurement?",
 answer:
 "Yes. We work with councils, housing associations, facilities managers and main contractors under framework agreements, with RAMS, insurances, PAS 2035 coordination, TrustMark lodgement and reporting expected at that level. A current capability statement is available on request.",
 },
 {
 question: "Do you handle portfolio rollouts across multiple properties?",
 answer:
 "Yes. We mobilise whole-stock surveys, sequence Target Energy Pathway design and coordinated install programmes across properties, rather than isolated one-off measures. Programme adherence, H&S and snagging are reported back to the buyer.",
 },
 {
 question: "How do PAS 2035, PAS 2030 and TrustMark lodgement work on your programmes?",
 answer:
 "PAS 2035 governs the coordinated retrofit process, assessment, design and measure sequencing. PAS 2030 applies to the installation standards for insulation and ventilation works. TrustMark lodgement is completed on completion so the works are registered and evidenced. We document each stage for your diligence pack.",
 },
 {
 question: "Can you report carbon and TEP outcomes in our format?",
 answer:
 "Yes. Carbon and Target Energy Pathway outcomes are reported per project or per portfolio in the buyer's preferred format. Claims are evidenced against the agreed reporting framework, not quoted generically.",
 },
 {
 question: "Do you provide social-value reporting?",
 answer:
 "Yes. We report on local jobs, apprenticeships, local supply chain and community benefit on a per-project or per-portfolio basis. Specifics are agreed up front to match the buyer's social-value framework.",
 },
 {
 question: "Is a capability statement available?",
 answer:
 "Yes. We provide a current capability statement and accreditation pack on request, with renewal dates for each accreditation and insurance to reduce diligence friction.",
 },
];

function PrimaryCta({ label = "Request A Feasibility Assessment" }: { label?: string }) {
 return (
 <a
 href="#bottom-cta"
 className="inline-flex items-center justify-center gap-2 rounded-full bg-accent text-accent-foreground px-6 py-3.5 font-semibold shadow-cta hover:bg-accent/90 transition-colors w-full sm:w-auto"
 >
 {label}
 <ArrowRight className="h-4 w-4" aria-hidden />
 </a>
 );
}

function SecondaryCta(_props?: { label?: string; ghost?: boolean }) {
  // Capability statement / accreditation pack downloads removed site-wide.
  return null;
}

const SECTORS = [
 "Council",
 "Housing Association",
 "Facilities Management",
 "Main Contractor",
 "Commercial",
 "Agriculture",
] as const;

function B2BEnquiryForm({ idPrefix = "b2b" }: { idPrefix?: string }) {
 const submit = useServerFn(submitEnquiry);
 const navigate = useNavigate();
 const [status, setStatus] = useState<"idle" | "submitting" | "error">("idle");
 const [formError, setFormError] = useState<string | null>(null);
 const [errors, setErrors] = useState<Record<string, string>>({});

 async function handleSubmit(e: FormEvent<HTMLFormElement>) {
 e.preventDefault();
 setStatus("submitting");
 setErrors({});
 setFormError(null);

 const fd = new FormData(e.currentTarget);
 const organisation = ((fd.get("organisation") as string) || "").trim();
 const sector = ((fd.get("sector") as string) || "").trim();
 const userMessage = ((fd.get("message") as string) || "").trim();

 const composedMessage = [
 organisation ? `Organisation: ${organisation}` : null,
 sector ? `Sector: ${sector}` : null,
 userMessage ? `\n${userMessage}` : null,
 ]
 .filter(Boolean)
 .join("\n");

 const raw = {
 form_type: "survey" as const,
 name: ((fd.get("name") as string) || "").trim() || undefined,
 phone: ((fd.get("phone") as string) || "").trim() || undefined,
 email: ((fd.get("email") as string) || "").trim() || undefined,
 postcode: ((fd.get("postcode") as string) || "").trim() || undefined,
 service_of_interest: "Whole-House Retrofit & Decarbonisation",
 message: composedMessage || undefined,
 source_page: SOURCE_PAGE,
 honeypot: (fd.get("company_website") as string) || "",
 };

 if (!organisation) {
 setErrors({ organisation: "Organisation is required" });
 setStatus("idle");
 return;
 }
 if (!sector) {
 setErrors({ sector: "Please select a sector" });
 setStatus("idle");
 return;
 }

 const parsed = submissionSchema.safeParse(raw);
 if (!parsed.success) {
 const fieldErrors: Record<string, string> = {};
 for (const issue of parsed.error.issues) {
 fieldErrors[String(issue.path[0] ?? "form")] = issue.message;
 }
 setErrors(fieldErrors);
 setStatus("idle");
 return;
 }

 try {
 const result = await submit({ data: parsed.data });
 if (result.success) {
   if (result.id) crsLeadSubmitted({ leadId: result.id, formId: "b2b_retrofit", leadType: "b2b" });
 toast.success("Enquiry sent", {
 description: "Thanks - the commercial team will be in touch shortly.",
 });
 navigate({ to: "/thank-you" });
 } else {
 setFormError(result.error ?? "Something went wrong. Please try again.");
 setStatus("error");
 }
 } catch (err) {
 console.error(err);
 setFormError("Network error. Please call us on 0800 009 2992.");
 setStatus("error");
 }
 }

 const labelCls = "block text-sm font-semibold mb-1.5";
 const inputCls =
 "w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring";
 const errCls = "text-xs text-destructive mt-1";

 return (
 <form onSubmit={handleSubmit} className="grid sm:grid-cols-2 gap-3 sm:gap-4" noValidate>
 <input
 type="text"
 name="company_website"
 tabIndex={-1}
 autoComplete="off"
 className="hidden"
 aria-hidden
 />

 <div>
 <label htmlFor={`${idPrefix}-name`} className={labelCls}>
 Name <span className="text-destructive">*</span>
 </label>
 <input id={`${idPrefix}-name`} name="name" required className={inputCls} />
 {errors.name && <p className={errCls}>{errors.name}</p>}
 </div>
 <div>
 <label htmlFor={`${idPrefix}-org`} className={labelCls}>
 Organisation <span className="text-destructive">*</span>
 </label>
 <input id={`${idPrefix}-org`} name="organisation" required className={inputCls} />
 {errors.organisation && <p className={errCls}>{errors.organisation}</p>}
 </div>
 <div className="sm:col-span-2">
 <label htmlFor={`${idPrefix}-sector`} className={labelCls}>
 Sector <span className="text-destructive">*</span>
 </label>
 <select id={`${idPrefix}-sector`} name="sector" required defaultValue="" className={inputCls}>
 <option value="" disabled>
 Select sector
 </option>
 {SECTORS.map((s) => (
 <option key={s} value={s}>
 {s}
 </option>
 ))}
 </select>
 {errors.sector && <p className={errCls}>{errors.sector}</p>}
 </div>
 <div>
 <label htmlFor={`${idPrefix}-phone`} className={labelCls}>
 Phone <span className="text-destructive">*</span>
 </label>
 <input id={`${idPrefix}-phone`} name="phone" required inputMode="tel" className={inputCls} />
 {errors.phone && <p className={errCls}>{errors.phone}</p>}
 </div>
 <div>
 <label htmlFor={`${idPrefix}-email`} className={labelCls}>
 Email <span className="text-destructive">*</span>
 </label>
 <input id={`${idPrefix}-email`} name="email" required type="email" className={inputCls} />
 {errors.email && <p className={errCls}>{errors.email}</p>}
 </div>
 <div className="sm:col-span-2">
 <label htmlFor={`${idPrefix}-postcode`} className={labelCls}>
 Site postcode <span className="text-destructive">*</span>
 </label>
 <input id={`${idPrefix}-postcode`} name="postcode" required className={inputCls} />
 {errors.postcode && <p className={errCls}>{errors.postcode}</p>}
 </div>
 <div className="sm:col-span-2">
 <label htmlFor={`${idPrefix}-msg`} className={labelCls}>
 Anything we should know? (optional)
 </label>
 <textarea id={`${idPrefix}-msg`} name="message" rows={3} className={inputCls} />
 </div>

 {formError && (
 <div className="sm:col-span-2 rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
 {formError}
 </div>
 )}

 <div className="sm:col-span-2">
 <button
 type="submit"
 disabled={status === "submitting"}
 className="inline-flex items-center justify-center gap-2 rounded-full bg-accent text-accent-foreground px-6 py-3.5 font-semibold shadow-cta hover:bg-accent/90 transition-colors w-full disabled:opacity-60"
 >
 {status === "submitting" ? (
 <>
 <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
 Sending...
 </>
 ) : (
 <>
 Request Feasibility Assessment <ArrowRight className="h-4 w-4" aria-hidden />
 </>
 )}
 </button>
 </div>
 </form>
 );
}

const HERO_TRUST = [
 "PAS 2035 Coordinated",
 "TrustMark Registered",
 "TEP-Aligned",
 "Framework-Ready",
 "Insurance-Backed",
];

const SECTOR_CARDS = [
 {
 Icon: Landmark,
 title: "Councils",
 body: "Whole-house decarbonisation programmes across housing stock, schools and operational sites. Framework delivery aligned to WHQS 2023 and Target Energy Pathways.",
 },
 {
 Icon: Building2,
 title: "Housing Associations",
 body: "Portfolio retrofit rollout across social-housing stock, with resident comms, ORP coordination and completion reporting.",
 },
 {
 Icon: HardHat,
 title: "Facilities Management",
 body: "Coordinated retrofit across managed estates, sequenced with planned maintenance windows and tenant operations.",
 },
 {
 Icon: ClipboardCheck,
 title: "Main Contractors",
 body: "Multi-measure retrofit packages delivered to programme, with RAMS, ITPs and snagging discipline expected on tier-one sites.",
 },
 {
 Icon: Home,
 title: "Registered Providers",
 body: "Fabric-first programmes for EPC uplift, WHQS energy targets and decarbonisation pathways across mixed stock.",
 },
 {
 Icon: Users,
 title: "Local Authorities",
 body: "Warm Homes, LA Flex and broader retrofit programme delivery with social-value and KPI reporting.",
 },
];

const WHY_CARDS = [
 {
 Icon: Layers,
 title: "Fabric-First, Worst-First",
 body: "Measures sequenced to the property, in line with the optimised retrofit approach.",
 },
 {
 Icon: Cpu,
 title: "Coordinated, Multi-Measure",
 body: "Design, measures and ventilation joined up rather than sold separately.",
 },
 {
 Icon: TreePine,
 title: "Evidenced Carbon & Compliance",
 body: "Outcomes documented against the buyer's reporting framework, not generic claims.",
 },
];

const SCOPE_CARDS = [
 {
 Icon: ClipboardList,
 title: "Retrofit Assessment & Coordination",
 body: "Whole-stock survey support, assessment, coordination and design involvement.",
 },
 {
 Icon: Home,
 title: "Fabric Measures",
 body: "Cavity, loft and internal wall insulation to standard.",
 },
 {
 Icon: Leaf,
 title: "Low-Carbon Heating",
 body: "Heat pumps and heating upgrades within the pathway.",
 },
 {
 Icon: Wind,
 title: "Ventilation & Moisture-Safe",
 body: "Whole-house ventilation designed alongside fabric, so airtightness and airflow stay balanced.",
 },
 {
 Icon: Sun,
 title: "Solar & Battery",
 body: "Generation and storage where the pathway supports it.",
 },
 {
 Icon: Gauge,
 title: "Monitoring & TEP Lodgement",
 body: "Post-works monitoring, TrustMark lodgement and reporting in the buyer's format.",
 },
];

const MOBILISATION = [
 {
 Icon: FileText,
 title: "Assessment & Surveys",
 body: "Whole-stock or single-property assessment, fabric surveys and coordination under PAS 2035.",
 },
 {
 Icon: ClipboardCheck,
 title: "TEP Design & Pathway",
 body: "Target Energy Pathway design, measure sequencing and ventilation strategy before install begins.",
 },
 {
 Icon: CalendarCheck,
 title: "Programme & RAMS",
 body: "Programme aligned to the buyer's schedule with full RAMS, ITPs, tenant liaison and site induction.",
 },
 {
 Icon: HardHat,
 title: "Coordinated Delivery",
 body: "Dedicated site management, multi-trade coordination and daily reporting under framework agreements.",
 },
];

const KPI_CARDS = [
 {
 Icon: CalendarCheck,
 title: "Programme Adherence",
 body: "Weekly programme reporting against the agreed schedule, with early-warning of risk.",
 },
 {
 Icon: ShieldCheck,
 title: "Health & Safety",
 body: "RAMS, ITPs, permits, induction and incident reporting documented and shared.",
 },
 {
 Icon: ClipboardCheck,
 title: "Snagging & Quality",
 body: "Snag list management with closure evidence, photo records and sign-off.",
 },
 {
 Icon: Gauge,
 title: "Commissioning & Records",
 body: "Commissioning records, PAS 2030 sign-off, TrustMark lodgement and monitoring data on completion.",
 },
 {
 Icon: TreePine,
 title: "Carbon Reporting",
 body: "Project-level carbon reporting in the buyer's format. Claims evidenced, not generic.",
 },
 {
 Icon: Users,
 title: "Social Value Reporting",
 body: "Local jobs, apprenticeships, supply chain and community benefit reported per project.",
 },
];

const COMPLIANCE_ROWS: { name: string; status: string; renewal: string; cover?: string }[] = [
 { name: "PAS 2035 Retrofit Coordinator", status: "Active", renewal: "" },
 { name: "PAS 2030 (Insulation & Ventilation)", status: "Active", renewal: "" },
 { name: "TrustMark Registration", status: "Active", renewal: "" },
 { name: "MCS Certification (Heat & Solar)", status: "Active", renewal: "" },
 { name: "Gas Safe Register", status: "Active", renewal: "" },
 { name: "Public / Product Liability Insurance", status: "Active", renewal: "", cover: "£5,000,000" },
 { name: "Employers' Liability Insurance", status: "Active", renewal: "", cover: "£10,000,000" },
 { name: "Professional Indemnity", status: "Active", renewal: "", cover: "£2,000,000" },
 { name: "Insurance-Backed Guarantee Provider", status: "Active", renewal: "" },
 { name: "CHAS / SafeContractor (or equivalent)", status: "On request", renewal: "" },
 { name: "ISO 9001 / 14001 / 45001", status: "On request", renewal: "" },
];

const SOCIAL_VALUE_CARDS = [
 {
 Icon: Users,
 title: "Local Jobs",
 body: "We hire, train and supply from the local economy on framework projects.",
 },
 {
 Icon: HardHat,
 title: "Apprenticeships",
 body: "Apprentice involvement on installs where the programme allows, supporting the next generation of installers.",
 },
 {
 Icon: Handshake,
 title: "Local Supply Chain",
 body: "Preferential use of regional suppliers, scaffold and access partners.",
 },
 {
 Icon: TreePine,
 title: "Community Benefit",
 body: "Community-facing actions agreed up front with the buyer, reported on completion.",
 },
];

const PROCESS_STEPS = [
 {
 step: 1,
 title: "Assessment & Whole-Stock Survey",
 description:
 "Whole-stock or property-level assessment, fabric surveys and coordination under PAS 2035.",
 },
 {
 step: 2,
 title: "Design & Target Energy Pathway",
 description:
 "Target Energy Pathway design, measure sequencing and ventilation strategy agreed before install.",
 },
 {
 step: 3,
 title: "Coordinated Install",
 description:
 "Framework-grade multi-measure install with RAMS, ITPs, daily reporting and PAS 2030 standards.",
 },
 {
 step: 4,
 title: "Monitoring, Lodgement & Reporting",
 description:
 "Post-works monitoring, TrustMark lodgement, carbon and social-value reporting in the buyer's format.",
 },
];

const B2B_TESTIMONIALS = [
 {
 quote:
 "CRS ran the whole-stock survey and coordinated fabric, heating and ventilation as one programme, not three separate contractors chasing each other.",
 author: "Asset Manager, Housing Association",
 },
 {
 quote:
 "TrustMark lodgement and TEP reporting came back in our format, with the evidence we needed for board without us chasing paperwork.",
 author: "Decarbonisation Lead, Local Authority",
 },
 {
 quote:
 "Mobilised under our framework with disciplined RAMS, weekly programme reporting and PAS 2030 sign-off we could actually audit.",
 author: "Project Manager, Main Contractor",
 },
];

export function LpCommercialRetrofit() {
 return (
 <>
 <PageBreadcrumb
 items={commercialBreadcrumbTrail("Whole-House Retrofit & Decarbonisation", "/commercial-retrofit")}
 />

 <section className="relative overflow-hidden">
 <div className="absolute inset-0">
 <img
 src={heroCommercialRetrofit}
 alt="Whole-house retrofit works on UK social housing stock"
 className="w-full h-full object-cover"
 width={1920}
 height={1280}
 loading="eager"
 decoding="async"
 fetchPriority="high"
 />
 <div className="absolute inset-0 bg-gradient-to-r from-primary/92 to-primary/70" />
 <div className="absolute inset-0 bg-gradient-to-b from-primary/40 via-primary/25 to-primary/35 md:from-primary/25 md:to-transparent" />
 </div>

 <div className="relative container-page py-8 md:py-12 lg:py-16">
 <div className="grid lg:grid-cols-5 gap-8 md:gap-10 items-start">
 <div className="lg:col-span-3 text-primary-foreground">
 <div className="mb-5 md:mb-6">
 <img
 src={crsLogo}
 alt="CRS Energy"
 width={953}
 height={408}
 className="h-20 md:h-28 lg:h-32 w-auto"
 loading="eager"
 decoding="async"
 />
 </div>

 <p className="text-xs font-bold uppercase tracking-[0.2em] text-accent mb-3">
 PAS 2035 Coordinated Retrofit, Councils, HAs &amp; Frameworks
 </p>
 <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-balance leading-[1.05]">
 Whole-House Retrofit &amp; Decarbonisation
 </h1>
 <p className="mt-4 md:mt-5 text-base md:text-lg lg:text-xl text-primary-foreground max-w-2xl text-pretty">
 Fabric-first, coordinated retrofit aligned to WHQS 2023, Target Energy Pathways and
 ORP, designed, delivered and documented as one programme rather than isolated
 measures.
 </p>

 <div className="mt-6 md:mt-7 flex flex-col sm:flex-row sm:flex-wrap gap-3">
 <PrimaryCta />
 <SecondaryCta ghost />
 </div>

 <ul className="hidden lg:flex flex-col gap-2 mt-7 rounded-2xl border border-white/20 bg-primary-foreground/5 backdrop-blur-sm p-4 max-w-md">
 {HERO_TRUST.map((item) => (
 <li
 key={item}
 className="flex items-center gap-3 text-base font-semibold text-primary-foreground"
 >
 <ShieldCheck className="h-5 w-5 text-accent shrink-0" aria-hidden />
 <span>{item}</span>
 </li>
 ))}
 </ul>
 <ul className="lg:hidden mt-5 flex flex-wrap gap-2">
 {HERO_TRUST.map((item) => (
 <li
 key={item}
 className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 backdrop-blur-sm px-3 py-1.5 text-xs font-semibold text-primary-foreground"
 >
 <CheckCircle2 className="h-3.5 w-3.5 text-accent" aria-hidden />
 {item}
 </li>
 ))}
 </ul>
 </div>

 <div className="lg:col-span-2 p-4 sm:p-5 md:p-6 rounded-2xl bg-primary-foreground/10 backdrop-blur-xl border border-white/20 shadow-xl">
 <p className="text-xs uppercase font-bold tracking-wider mb-1 text-accent">B2B Enquiry</p>
 <h2 className="text-lg sm:text-xl font-bold mb-1 text-primary-foreground">
 Request A Feasibility Assessment
 </h2>
 <p className="text-sm text-primary-foreground/90 mb-4">
 For commercial, social-housing and framework enquiries. We&apos;ll respond from the
 commercial team.
 </p>
 <div className="[&_label]:text-white [&_input]:bg-white/15 [&_input]:text-white [&_input]:border-white/50 [&_input::placeholder]:text-white/75 [&_select]:bg-white/15 [&_select]:text-white [&_select]:border-white/50 [&_select]:[color-scheme:dark] [&_select_option]:bg-white [&_select_option]:text-foreground [&_textarea]:bg-white/15 [&_textarea]:text-white [&_textarea]:border-white/50">
 <B2BEnquiryForm idPrefix="hero" />
 </div>
 </div>
 </div>
 </div>
 </section>

 <section className="border-b border-border/55 bg-secondary/25 py-8">
 <div className="container-page">
 <p className="text-center text-xs font-semibold tracking-[0.2em] uppercase text-muted-foreground mb-5">
 PAS 2035 Coordinated, TrustMark Registered, Framework-Ready
 </p>
 <AccreditationStrip />
 </div>
 </section>

 <section className="border-b border-border/40 bg-background py-12 md:py-16">
 <div className="container-page">
 <div className="max-w-3xl mb-8 md:mb-10">
 <p className="text-xs font-bold uppercase tracking-widest text-accent mb-2">
 Who We Work With
 </p>
 <h2 className="text-2xl md:text-4xl font-bold text-foreground leading-tight">
 Sectors We Deliver Into
 </h2>
 <p className="text-sm md:text-base text-muted-foreground mt-3">
 Whole-house retrofit delivered for buyers who need PAS 2035 coordination, TrustMark
 lodgement and framework-grade reporting, not isolated measures sold separately.
 </p>
 </div>
 <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
 {SECTOR_CARDS.map((c) => (
 <article key={c.title} className="rounded-2xl border border-border bg-card p-6 shadow-sm">
 <div className="w-11 h-11 rounded-lg bg-accent/15 flex items-center justify-center mb-4">
 <c.Icon className="h-5 w-5 text-accent" aria-hidden />
 </div>
 <h3 className="font-bold text-foreground text-base md:text-lg mb-2">{c.title}</h3>
 <p className="text-sm text-muted-foreground leading-relaxed">{c.body}</p>
 </article>
 ))}
 </div>
 </div>
 </section>

 <section className="border-b border-border/40 bg-secondary/25 py-12 md:py-16">
 <div className="container-page">
 <div className="max-w-3xl mb-8 md:mb-10">
 <p className="text-xs font-bold uppercase tracking-widest text-accent mb-2">
 Why It Matters
 </p>
 <h2 className="text-2xl md:text-4xl font-bold text-foreground leading-tight">
 Fabric-First, Coordinated, Evidenced
 </h2>
 <p className="text-sm md:text-base text-muted-foreground mt-3">
 Procurement frameworks differ by nation. In Wales we align to WHQS 2023, Target Energy
 Pathways and the Optimised Retrofit Programme (ORP). In the South West we work to
 Decent Homes standards and emerging Renters&apos; Rights Bill expectations. Every
 carbon claim is evidenced per project, not quoted generically.
 </p>
 </div>
 <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
 {WHY_CARDS.map((c) => (
 <article key={c.title} className="rounded-2xl border border-border bg-card p-6 shadow-sm">
 <div className="w-11 h-11 rounded-lg bg-accent/15 flex items-center justify-center mb-4">
 <c.Icon className="h-5 w-5 text-accent" aria-hidden />
 </div>
 <h3 className="font-bold text-foreground text-base md:text-lg mb-2">{c.title}</h3>
 <p className="text-sm text-muted-foreground leading-relaxed">{c.body}</p>
 </article>
 ))}
 </div>
 </div>
 </section>

 <section className="border-b border-border/40 bg-background py-12 md:py-16">
 <div className="container-page">
 <div className="max-w-3xl mb-8 md:mb-10">
 <p className="text-xs font-bold uppercase tracking-widest text-accent mb-2">Scope</p>
 <h2 className="text-2xl md:text-4xl font-bold text-foreground leading-tight">
 What We Deliver
 </h2>
 <p className="text-sm md:text-base text-muted-foreground mt-3">
 From single-property retrofit to whole-stock programmes, fabric, heating, ventilation
 and generation coordinated under one PAS 2035 pathway.
 </p>
 </div>
 <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
 {SCOPE_CARDS.map((c) => (
 <article key={c.title} className="rounded-2xl border border-border bg-card p-5 shadow-sm">
 <div className="flex items-start gap-3">
 <div className="w-10 h-10 rounded-lg bg-accent/15 flex items-center justify-center shrink-0">
 <c.Icon className="h-5 w-5 text-accent" aria-hidden />
 </div>
 <div>
 <h3 className="font-bold text-foreground text-base mb-1">{c.title}</h3>
 <p className="text-sm text-muted-foreground leading-relaxed">{c.body}</p>
 </div>
 </div>
 </article>
 ))}
 </div>
 </div>
 </section>

 <section className="border-b border-border/40 bg-secondary/25 py-12 md:py-16">
 <div className="container-page">
 <div className="max-w-3xl mb-8 md:mb-10">
 <p className="text-xs font-bold uppercase tracking-widest text-accent mb-2">
 Mobilisation &amp; Delivery
 </p>
 <h2 className="text-2xl md:text-4xl font-bold text-foreground leading-tight">
 Coordination Is The Differentiator
 </h2>
 <p className="text-sm md:text-base text-muted-foreground mt-3">
 PAS 2035 assessment, TEP design, tenant liaison, programme, RAMS and multi-trade
 coordination, all run as one programme under framework agreements.
 </p>
 </div>
 <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
 {MOBILISATION.map((c) => (
 <article key={c.title} className="rounded-2xl border border-border bg-card p-5 shadow-sm">
 <div className="w-10 h-10 rounded-lg bg-accent/15 flex items-center justify-center mb-3">
 <c.Icon className="h-5 w-5 text-accent" aria-hidden />
 </div>
 <h3 className="font-bold text-foreground text-sm md:text-base mb-1.5">{c.title}</h3>
 <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">{c.body}</p>
 </article>
 ))}
 </div>
 </div>
 </section>

 <section className="border-b border-border/40 bg-background py-12 md:py-16">
 <div className="container-page">
 <div className="max-w-3xl mb-8 md:mb-10">
 <p className="text-xs font-bold uppercase tracking-widest text-accent mb-2">
 KPIs &amp; Reporting
 </p>
 <h2 className="text-2xl md:text-4xl font-bold text-foreground leading-tight">
 Visible Performance, Documented Accountability
 </h2>
 <p className="text-sm md:text-base text-muted-foreground mt-3">
 Buyers see what&apos;s happening on programme, on H&amp;S, on quality, on commissioning
 and lodgement, and on social value, in formats their boards and panels expect.
 </p>
 </div>
 <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
 {KPI_CARDS.map((c) => (
 <article key={c.title} className="rounded-2xl border border-border bg-card p-5 shadow-sm">
 <div className="w-10 h-10 rounded-lg bg-accent/15 flex items-center justify-center mb-3">
 <c.Icon className="h-5 w-5 text-accent" aria-hidden />
 </div>
 <h3 className="font-bold text-foreground text-base mb-1.5">{c.title}</h3>
 <p className="text-sm text-muted-foreground leading-relaxed">{c.body}</p>
 </article>
 ))}
 </div>
 </div>
 </section>

 <section className="border-b border-border/40 bg-secondary/25 py-12 md:py-16">
 <div className="container-page max-w-5xl">
 <div className="max-w-3xl mb-8">
 <p className="text-xs font-bold uppercase tracking-widest text-accent mb-2">Compliance</p>
 <h2 className="text-2xl md:text-4xl font-bold text-foreground leading-tight">
 Accreditations &amp; Insurances
 </h2>
 <p className="text-sm md:text-base text-muted-foreground mt-3">
 PAS 2035 coordination, PAS 2030 install standards and TrustMark lodgement, with
 renewal dates published so your procurement team isn&apos;t chasing.
 </p>
 </div>

 <div className="overflow-x-auto rounded-2xl border border-border bg-card shadow-sm">
 <table className="w-full text-left text-sm">
 <thead className="bg-secondary/40 text-foreground">
 <tr>
 <th scope="col" className="px-4 py-3 font-bold">
 Accreditation / Insurance
 </th>
 <th scope="col" className="px-4 py-3 font-bold">
 Status
 </th>
 <th scope="col" className="px-4 py-3 font-bold">
 Cover
 </th>
 <th scope="col" className="px-4 py-3 font-bold">
 Renewal
 </th>
 </tr>
 </thead>
 <tbody>
 {COMPLIANCE_ROWS.map((row) => (
 <tr key={row.name} className="border-t border-border">
 <td className="px-4 py-3 font-medium text-foreground">{row.name}</td>
 <td className="px-4 py-3 text-muted-foreground">{row.status}</td>
 <td className="px-4 py-3 text-muted-foreground">{row.cover || "-"}</td>
 <td className="px-4 py-3 text-muted-foreground">{row.renewal}</td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>

 <div className="mt-6 flex flex-col sm:flex-row gap-3 items-start">
 <SecondaryCta label="Download Capability Statement / Accreditation Pack" />
 </div>
 </div>
 </section>

 <section className="bg-primary text-primary-foreground py-14 md:py-20">
 <div className="container-page">
 <div className="max-w-3xl mb-8 md:mb-10">
 <div className="flex items-center gap-2 mb-3 text-accent">
 <Users className="h-5 w-5" aria-hidden />
 <p className="text-xs font-bold uppercase tracking-widest">Social Value</p>
 </div>
 <h2 className="text-2xl md:text-4xl font-bold leading-tight">
 Reported In Your Format, Evidenced Per Project
 </h2>
 <p className="text-base md:text-lg text-primary-foreground/90 mt-3 leading-relaxed">
 Social value isn&apos;t an afterthought. It&apos;s agreed up front against your
 framework&apos;s measures and reported on completion, with the evidence to back it up.
 </p>
 </div>
 <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
 {SOCIAL_VALUE_CARDS.map((c) => (
 <article
 key={c.title}
 className="rounded-2xl border border-white/15 bg-white/5 backdrop-blur-sm p-5"
 >
 <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center mb-3">
 <c.Icon className="h-5 w-5 text-accent" aria-hidden />
 </div>
 <h3 className="font-bold text-primary-foreground text-base mb-1.5">{c.title}</h3>
 <p className="text-sm text-primary-foreground/85 leading-relaxed">{c.body}</p>
 </article>
 ))}
 </div>
 </div>
 </section>

 <section className="border-b border-border/40 bg-background py-12 md:py-16">
 <div className="container-page">
 <div className="max-w-3xl mb-8 md:mb-10">
 <p className="text-xs font-bold uppercase tracking-widest text-accent mb-2">
 Case Studies
 </p>
 <h2 className="text-2xl md:text-4xl font-bold text-foreground leading-tight">
 Recent Projects
 </h2>
 <p className="text-sm md:text-base text-muted-foreground mt-3">
 Selected retrofit and decarbonisation programmes with scope, risks managed and outcomes.{" "}
 [Add real projects with before/after photos, scope notes and buyer outcomes.]
 </p>
 </div>
 <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
 {[1, 2, 3].map((n) => (
 <article
 key={n}
 className="rounded-2xl border border-dashed border-border bg-card p-6 shadow-sm"
 >
 <div className="aspect-video w-full rounded-lg bg-secondary/40 mb-4 flex items-center justify-center">
 <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
 [Add Project Photo]
 </span>
 </div>
 <h3 className="font-bold text-foreground mb-1">[Case Study {n} Title]</h3>
 <p className="text-xs text-muted-foreground mb-2">
 [Sector] - [Location] - [Scope]
 </p>
 <p className="text-sm text-muted-foreground leading-relaxed">
 [Scope, risks managed, programme adherence, TEP / lodgement / social-value
 outcomes. Replace with real project content.]
 </p>
 </article>
 ))}
 </div>
 </div>
 </section>

 <section className="border-b border-border/40 bg-secondary/35 py-12 md:py-16">
 <div className="container-page max-w-5xl">
 <div className="rounded-2xl border-2 border-dashed border-accent/60 bg-card p-6 md:p-8 shadow-sm">
 <div className="flex items-center gap-2 mb-3">
 <PoundSterling className="h-5 w-5 text-accent" aria-hidden />
 <p className="text-xs font-bold uppercase tracking-widest text-accent">
 Funding &amp; Programme Finance (B2B)
 </p>
 </div>
 <h2 className="text-2xl md:text-3xl font-bold text-foreground leading-tight mb-4">
 Grant-Funded, Capital Works Or Phased Delivery
 </h2>
 <p className="text-sm md:text-base text-muted-foreground leading-relaxed mb-4">
 Whole-house retrofit can be delivered through grant-funded programmes, direct capital
 works or phased stock rollout, depending on the buyer&apos;s funding route and
 programme structure.
 </p>

 <div className="rounded-xl border border-amber-300/70 bg-amber-50 dark:bg-amber-950/30 p-4 mb-5">
 <p className="text-xs font-bold uppercase tracking-wider text-amber-900 dark:text-amber-200 mb-2">
 Programme-Specific Funding Only
 </p>
 <p className="text-sm text-amber-900/90 dark:text-amber-100/90 leading-relaxed">
 Grant eligibility, funding rates and programme terms vary by scheme and property.
 Carbon outcomes and cost evidence are confirmed per project in writing, never
 quoted generically.
 </p>
 </div>

 <div className="grid sm:grid-cols-3 gap-4">
 {[
 {
 title: "Grant Programmes",
 body: "Warm Homes, LA Flex and framework-funded retrofit where eligibility applies.",
 },
 {
 title: "Capital Works",
 body: "Direct capital delivery with written quotation and programme-level reporting.",
 },
 {
 title: "Phased Rollout",
 body: "Stock sequenced by priority, voids or pathway stage, reported per phase.",
 },
 ].map((opt) => (
 <article key={opt.title} className="rounded-xl border border-border bg-background p-4">
 <h3 className="font-bold text-foreground mb-1.5">{opt.title}</h3>
 <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
 {opt.body}
 </p>
 </article>
 ))}
 </div>
 </div>
 </div>
 </section>

 <section className="border-b border-border bg-card py-10 md:py-16">
 <div className="container-page">
 <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6 md:mb-8 text-center">
 Our Process
 </h2>
 <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
 {PROCESS_STEPS.map((s) => (
 <div key={s.step} className="rounded-2xl border border-border bg-background p-5 shadow-sm">
 <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-base font-extrabold mb-4">
 {s.step}
 </div>
 <h3 className="font-bold text-foreground mb-2">{s.title}</h3>
 <p className="text-sm text-muted-foreground leading-relaxed">{s.description}</p>
 </div>
 ))}
 </div>
 </div>
 </section>

 <section className="border-b border-border/40 bg-background py-12 md:py-16">
 <div className="container-page">
 <div className="max-w-3xl mb-8 md:mb-10">
 <div className="flex items-center gap-2 mb-3">
 <ShieldCheck className="h-5 w-5 text-accent" aria-hidden />
 <p className="text-xs font-bold uppercase tracking-widest text-accent">
 Trust &amp; Track Record
 </p>
 </div>
 <h2 className="text-2xl md:text-4xl font-bold text-foreground leading-tight">
 What Procurement, Asset Managers &amp; Main Contractors Say
 </h2>
 </div>
 <div className="grid lg:grid-cols-3 gap-5">
 {B2B_TESTIMONIALS.map((t) => (
 <figure key={t.author} className="rounded-2xl border border-border bg-card p-6 shadow-sm">
 <blockquote className="text-sm md:text-base text-foreground leading-relaxed">
 &ldquo;{t.quote}&rdquo;
 </blockquote>
 <figcaption className="mt-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
 {t.author}
 </figcaption>
 </figure>
 ))}
 </div>
 </div>
 </section>

 <section className="border-b border-border/40 bg-secondary/25 py-12 md:py-16">
 <div className="container-page max-w-4xl">
 <div className="mb-8">
 <p className="text-xs font-bold uppercase tracking-widest text-accent mb-2">FAQ</p>
 <h2 className="text-2xl md:text-4xl font-bold text-foreground leading-tight">
 Procurement &amp; Delivery Questions
 </h2>
 </div>
 <dl className="space-y-4">
 {COMMERCIAL_RETROFIT_FAQS.map((f) => (
 <div key={f.question} className="rounded-2xl border border-border bg-card p-5 md:p-6 shadow-sm">
 <dt className="font-bold text-foreground text-base md:text-lg mb-2 flex items-start gap-2">
 <Wrench className="h-4 w-4 text-accent mt-1 shrink-0" aria-hidden />
 {f.question}
 </dt>
 <dd className="text-sm md:text-base text-muted-foreground leading-relaxed">
 {f.answer}
 </dd>
 </div>
 ))}
 </dl>
 </div>
 </section>

 <span id="contact" aria-hidden className="block scroll-mt-32" />
 <section
 id="bottom-cta"
 className="scroll-mt-32 border-t border-border/50 bg-gradient-to-b from-muted/30 to-secondary/35 py-12 md:py-20"
 >
 <div className="container-page max-w-3xl">
 <div className="text-center mb-8">
 <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
 Request A Feasibility Assessment
 </h2>
 <p className="text-sm md:text-base text-muted-foreground">
 For commercial, social-housing and framework enquiries. Or call{" "}
 <a href={PHONE_TEL} className="text-accent font-semibold">
 {PHONE_LABEL}
 </a>
 .
 </p>
 </div>
 <div className="rounded-2xl border border-border bg-card p-5 sm:p-6 md:p-8 shadow-soft">
 <B2BEnquiryForm idPrefix="bottom" />
 </div>
 <p className="text-xs text-muted-foreground text-center mt-5">
 Capability Statement and Accreditation Pack available on request.
 </p>
 </div>
 </section>
 </>
 );
}
